import React, { useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { TextV2 } from 'og-merchant-portal-react-library';

import GlobalStyle from './components/GlobalStyle/GlobalStyle';
import IntlProvider from './components/IntlProvider/IntlProvider';
import { getSessionStorage } from './utils/sessionHelper';
import {
  StyledActionContainer,
  StyledButton,
  StyledCard,
  StyledLogo,
  StyledPage,
  StyledTextContainer,
  StyledTitleContainer,
} from './Decommission.styled';

const { redirectUrl, redirectDelay = 15 } = window.appSettings.decommission || {};

const availableTranslations = (window.appSettings.availableLocales || 'en')
  .split(',')
  .map((locale) => locale.trim().toLowerCase())
  .filter(Boolean);

const getLanguage = () => {
  const browserLanguage = (global.navigator.language || '').split('-')[0];

  return (
    [getSessionStorage('locale'), browserLanguage]
      .filter(Boolean)
      .map((locale) => locale.toLowerCase())
      .find((locale) => availableTranslations.includes(locale)) || availableTranslations[0]
  );
};

const Decommission = () => {
  const [secondsLeft, setSecondsLeft] = useState(redirectDelay);
  const [theme, setTheme] = useState(null);
  const [language] = useState(getLanguage);

  useEffect(() => {
    fetch('/themes/theme.json', { cache: 'no-cache' })
      .then((response) => response.json())
      .then((theme) => {
        setTheme(theme);
      })
      .catch(() => {
        console.error('The portal theme could not be loaded. The redirection still applies.');
      });
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) {
      window.location.href = redirectUrl;
      return;
    }

    const timer = setTimeout(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const handleRedirectNow = () => {
    window.location.href = redirectUrl;
  };

  if (theme === null) {
    return null;
  }

  return (
    <IntlProvider language={language} availableTranslations={availableTranslations}>
      <ThemeProvider theme={theme}>
        <>
          <GlobalStyle />
          <StyledPage>
            <StyledCard>
              <StyledTitleContainer>
                <StyledLogo src="/themes/logo.svg" alt="" />
                <TextV2 weight="semibold" size="h5" color="title">
                  <FormattedMessage
                    id="global.decommission.title"
                    defaultMessage="This application has been decommissioned"
                  />
                </TextV2>
              </StyledTitleContainer>
              <StyledTextContainer>
                <TextV2 size="lg" color="primary" data-test="decommission_message">
                  <FormattedMessage
                    id="global.decommission.message"
                    defaultMessage="This application is no longer in use. Please use the Merchant Portal users module instead."
                  />
                </TextV2>
              </StyledTextContainer>
              <StyledTextContainer>
                <TextV2 size="md" color="inactive">
                  <FormattedMessage
                    id="global.decommission.redirect"
                    defaultMessage="You will be redirected automatically in {seconds, plural, one {# second} other {# seconds}}."
                    values={{ seconds: secondsLeft }}
                  />
                </TextV2>
              </StyledTextContainer>
              <StyledActionContainer>
                <StyledButton type="button" onClick={handleRedirectNow} data-testid="decommission_action_redirect">
                  <TextV2 size="subtitle" color="secondary">
                    <FormattedMessage id="global.decommission.action" defaultMessage="Go now" />
                  </TextV2>
                </StyledButton>
              </StyledActionContainer>
            </StyledCard>
          </StyledPage>
        </>
      </ThemeProvider>
    </IntlProvider>
  );
};

export default Decommission;
